import { useEffect, useMemo, useState } from "react";
import { colors } from "@styles/colors";
import type { Supplier } from "../types";

interface SuccessStepProps {
  supplier: Supplier | null;
  onDone: () => void;
}

const CONFETTI_COLORS = ["#EF4444", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899"];

/** 完成頁：藍色全螢幕 + 彩帶噴發。動畫需精細控制，以 inline style 呈現。 */
const SuccessStep = ({ supplier, onDone }: SuccessStepProps) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(t);
  }, []);

  const confetti = useMemo(
    () =>
      Array.from({ length: 50 }).map((_, i) => {
        const rad = ((i / 50) * 360 * Math.PI) / 180;
        const dist = 100 + Math.random() * 180;
        return {
          tx: Math.cos(rad) * dist,
          ty: Math.sin(rad) * dist - 80,
          delay: Math.random() * 0.4,
          dur: 0.8 + Math.random() * 0.6,
          size: 3 + Math.random() * 6,
          rot: Math.random() * 720,
          color: CONFETTI_COLORS[i % 6],
          type: i % 4,
        };
      }),
    [],
  );

  return (
    <div
      style={{
        minHeight: "100dvh",
        width: "100vw",
        background: colors.primary,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 32px",
        position: "relative",
        overflow: "hidden",
        fontFamily: "var(--font-sans)",
      }}>
      <style>{`
        @keyframes nu-burst{0%{transform:translate(0,0) rotate(0deg) scale(0);opacity:0}15%{opacity:1;transform:translate(var(--tx),var(--ty)) rotate(180deg) scale(1)}60%{opacity:1}100%{transform:translate(var(--tx),calc(var(--ty) + 300px)) rotate(var(--rot)) scale(0.5);opacity:0}}
        @keyframes nu-scaleIn{0%{transform:scale(0);opacity:0}50%{transform:scale(1.3)}70%{transform:scale(0.9)}100%{transform:scale(1);opacity:1}}
        @keyframes nu-fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {show &&
        confetti.map((c, i) => (
          <div
            key={i}
            style={
              {
                position: "absolute",
                top: "38%",
                left: "50%",
                zIndex: 1,
                "--tx": `${c.tx}px`,
                "--ty": `${c.ty}px`,
                "--rot": `${c.rot}deg`,
                animation: `nu-burst ${c.dur}s cubic-bezier(0.2,0.8,0.3,1) ${c.delay}s forwards`,
              } as React.CSSProperties
            }>
            {c.type === 0 && <div style={{ width: c.size, height: c.size * 3, borderRadius: 1.5, background: c.color }} />}
            {c.type === 1 && <div style={{ width: c.size * 1.5, height: c.size * 0.8, borderRadius: c.size, background: c.color }} />}
            {c.type === 2 && (
              <svg width={c.size * 2} height={c.size * 4} viewBox="0 0 8 16">
                <path d="M1 0Q7 4 1 8Q7 12 1 16" stroke={c.color} strokeWidth="2" fill="none" />
              </svg>
            )}
            {c.type === 3 && <div style={{ width: c.size, height: c.size, borderRadius: "50%", background: c.color }} />}
          </div>
        ))}

      <div
        style={{
          width: 88,
          height: 88,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: show ? "nu-scaleIn 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.2s both" : "none",
          marginBottom: 28,
          zIndex: 2,
        }}>
        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
            <path d="M10 20l7 7 13-13" stroke={colors.primary} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      <div style={{ zIndex: 2, textAlign: "center", animation: show ? "nu-fadeUp 0.5s ease 0.4s both" : "none" }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", marginBottom: 8 }}>註冊完成 🎉</div>
        <div style={{ fontSize: 15, color: "rgba(255,255,255,0.85)", lineHeight: 1.7 }}>
          已綁定 <span style={{ color: "#fff", fontWeight: 700 }}>{supplier?.company}</span>
          <br />
          現在可以開始訂購瓦斯囉
        </div>
      </div>

      <button
        onClick={onDone}
        style={{
          marginTop: 40,
          width: "100%",
          maxWidth: 300,
          padding: "16px",
          borderRadius: 14,
          border: "none",
          background: "#fff",
          color: colors.primary,
          fontSize: 16,
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          zIndex: 2,
          animation: show ? "nu-fadeUp 0.5s ease 0.6s both" : "none",
        }}>
        立即訂購 →
      </button>
    </div>
  );
};

export default SuccessStep;
